import { useEffect, useRef, useState, useCallback, useContext } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  Autocomplete,
  OverlayView,
  Polyline,
} from "@react-google-maps/api";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ActivityBar from "../../components/maps/ActivityBar";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useBeforeunload } from "react-beforeunload";
import { getDirectionsForItem } from "../../utils/helpers/getDirectionByAngle";
import { useGetAllDirections } from "../../hooks/query/useDirection";
import { useGetAllColors } from "../../hooks/query/useColor";
import { getColorHexCode } from "../../utils/helpers/getColorHexCode";
import ColorMenu from "./ColorMenu";
import DroppedItemsOverlay, { Corner } from "./DroppedItemsOverlay";
import {
  BOUNDARY_ANGLES,
  anglePairs,
  calculateDistance,
  getAngleOnSitePlan,
  pinSvg,
} from "./helpers";
import {
  getIntersectionPoint,
  intersectionPointsFromAnglePairs,
  latLngToXY,
  lineSegmentIntersection2D,
  xyToLatLng,
} from "../../utils/helpers/getIntersectionPoint";
import { useCreateMapState, useGetMapState } from "../../hooks/query/useVastu";
import { AuthContext } from "../../context/authContext";
import { toast } from "react-toastify";
import ConfirmDialog from "../../components/confirmation/ConfirmDialog";
import { useUpdateSitePlanRotation } from "../../hooks/query/useUploadSitePlan";
import Compass from "./MapCompass";
import { DroppedItem } from "./types";
import getAngleFromCentre from "../../utils/helpers/getAngleFromCentre";
import getClosestEntrance from "../../utils/helpers/getClosestEntrance ";

const defaultCenter = {
  lat: 27.722894,
  lng: 85.330762,
};

interface LatLng {
  lat: number;
  lng: number;
}

function MapContent() {
  const { currentUser } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [zoom, setZoom] = useState(() => {
    if (isMobile) return 19;
    if (isTablet) return 20;
    return 21;
  });

  const [pins, setPins] = useState<any[]>([]);
  const [marmaPoints, setMarmaPoints] = useState<any[]>([]);
  const [marmaLines, setMarmaLines] = useState<any[]>([]);
  const [angleMarkers, setAngleMarkers] = useState<LatLng[]>([]);

  const [showMarma, setShowMarma] = useState(false);
  const [isPinning, setIsPinning] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const [showCompass, setShowCompass] = useState(false);
  const [compassOpacity, setCompassOpacity] = useState<number>(1);

  const [history, setHistory] = useState<any[][]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);

  // color selection
  const [selectedItemId, setSelectedItemId] = useState("");
  const [colorAnchorEl, setColorAnchorEl] = useState<null | HTMLElement>(null);
  const [colorMenuOpen, setColorMenuOpen] = useState(false);

  const [sitePlanImage, setSitePlanImage] = useState<string | null>(null);
  const [sitePlanPoints, setSitePlanPoints] = useState<
    { x: number; y: number }[]
  >([]);
  const [mapPoints, setMapPoints] = useState<LatLng[]>([]);
  const [isMatchingSitePlanPoints, setIsMatchingSitePlanPoints] =
    useState(false);
  const [currentSitePlanPointIndex, setCurrentSitePlanPointIndex] =
    useState<number>(0);

  // dropped items
  const [droppedItems, setDroppedItems] = useState<DroppedItem[]>([]);

  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);
  const [resizingItemId, setResizingItemId] = useState<string | null>(null);
  const [rotatingItemId, setRotatingItemId] = useState<string | null>(null);

  const [resizingCorner, setResizingCorner] = useState<Corner | null>(null);

  const [initialLatLng, setInitialLatLng] = useState({ lat: 0, lng: 0 });
  const [initialMouse, setInitialMouse] = useState({ x: 0, y: 0 });
  const [initialSize, setInitialSize] = useState<{
    width: number;
    height: number;
  }>({
    width: 0,
    height: 0,
  });
  const [initialRotation, setInitialRotation] = useState<number>(0);

  const [hoveredEdge, setHoveredEdge] = useState<number | null>(null);
  const [selectedEdges, setSelectedEdges] = useState<number[]>([]);

  const [openClearDialog, setOpenClearDialog] = useState(false);

  const navigate = useNavigate();
  const { propertyId } = useParams();
  const location = useLocation();
  const { latitude, longitude } = location.state || {};
  const { data: directions } = useGetAllDirections();
  const { data: colors } = useGetAllColors();

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const [compassCenter, setCompassCenter] = useState<LatLng | null>(null);
  const { data: savedMapState, isLoading: isFetching } = useGetMapState(
    propertyId || "",
    "map"
  );

  const { mutate: saveMapState } = useCreateMapState();
  const { mutate: updateSitePlanRotation } = useUpdateSitePlanRotation();

  useEffect(() => {
    if (savedMapState) {
      if (
        savedMapState.mapState?.pins &&
        savedMapState.mapState.pins.length > 0
      ) {
        setIsPinning(false);
        setPins(savedMapState.mapState.pins);
      }
      if (savedMapState.mapState?.sitePlanPoints) {
        setSitePlanPoints(savedMapState.mapState.sitePlanPoints);
      }
      if (savedMapState.mapState?.mapPoints) {
        setMapPoints(savedMapState.mapState.mapPoints);
      }
      if (savedMapState.mapState?.droppedItems) {
        setDroppedItems(savedMapState.mapState.droppedItems);
      }
    }
  }, [savedMapState]);

  useEffect(() => {
    if (latitude && longitude) {
      const newLat = parseFloat(latitude);
      const newLng = parseFloat(longitude);
      if (!isNaN(newLat) && !isNaN(newLng)) {
        setZoom(21);
        setMapCenter({ lat: newLat, lng: newLng });
      }
    }
  }, [latitude, longitude]);

  function calculateCentroid(points: LatLng[]): LatLng {
    if (points.length < 3) return points[0];

    const centerRef = points[0];
    let xyPoints = points.map((pt) =>
      latLngToXY(pt.lat, pt.lng, centerRef.lat, centerRef.lng)
    );

    let centroidX = 0;
    let centroidY = 0;
    let signedArea = 0;

    for (let i = 0; i < xyPoints.length; i++) {
      const x0 = xyPoints[i].x;
      const y0 = xyPoints[i].y;
      const x1 = xyPoints[(i + 1) % xyPoints.length].x;
      const y1 = xyPoints[(i + 1) % xyPoints.length].y;

      const a = x0 * y1 - x1 * y0;
      signedArea += a;
      centroidX += (x0 + x1) * a;
      centroidY += (y0 + y1) * a;
    }

    signedArea *= 0.5;
    centroidX /= 6 * signedArea;
    centroidY /= 6 * signedArea;

    return xyToLatLng(centroidX, centroidY, centerRef.lat, centerRef.lng);
  }

  useEffect(() => {
    if (pins.length > 0) {
      const centroid = calculateCentroid(pins);
      setCompassCenter(centroid);
    } else {
      setCompassCenter(null);
    }
  }, [pins]);

  useEffect(() => {
    if (sitePlanPoints.length === 2 && mapPoints.length === 2) {
      const center = calculateCentroid(pins);
      const centerRef = { lat: center.lat, lng: center.lng };

      // convert mapPoints to XY
      const p1XY = latLngToXY(
        mapPoints[0].lat,
        mapPoints[0].lng,
        centerRef.lat,
        centerRef.lng
      );
      const p2XY = latLngToXY(
        mapPoints[1].lat,
        mapPoints[1].lng,
        centerRef.lat,
        centerRef.lng
      );
      const degree = getAngleOnSitePlan(p1XY, p2XY);

      const angleSite = getAngleOnSitePlan(
        sitePlanPoints[0],
        sitePlanPoints[1]
      );
      let rotation = degree - angleSite;
      rotation = (rotation + 360) % 360;

      const transform = {
        scale: 1,
        rotation,
        translation: { x: 0, y: 0 },
      };

      handleSaveMapState();
      if (propertyId) {
        updateSitePlanRotation({
          propertyId,
          rotationDegree: rotation,
        });
      }
      navigate(`/site-plan/${propertyId}`, {
        state: {
          sitePlanImage,
          transform,
        },
      });
      console.log("Rotation Degree", transform);

      setIsMatchingSitePlanPoints(false);
    }
  }, [sitePlanPoints, mapPoints, navigate, pins, propertyId, sitePlanImage]);

  useEffect(() => {
    if (pins.length > 2) {
      const center = calculateCentroid(pins);

      // intersection with boundary angles
      const intersections = BOUNDARY_ANGLES.map((angle) =>
        getIntersectionPoint(center, pins, angle)
      ).filter(Boolean) as LatLng[];

      // intersection from angle pairs
      const results = intersectionPointsFromAnglePairs(
        center,
        pins,
        anglePairs
      );
      const centerRef = { lat: center.lat, lng: center.lng };
      const segments: Array<[LatLng, LatLng]> = [];

      for (const [pA, pB] of results) {
        if (pA && pB) {
          segments.push([pA, pB]);
        }
      }

      // Convert each segment from lat/lng to XY
      const xySegments = segments.map(([pA, pB]) => [
        latLngToXY(pA.lat, pA.lng, centerRef.lat, centerRef.lng),
        latLngToXY(pB.lat, pB.lng, centerRef.lat, centerRef.lng),
      ]);

      const intersectionPointsXY: Array<{ x: number; y: number }> = [];
      for (let i = 0; i < xySegments.length; i++) {
        const [A1, A2] = xySegments[i];
        for (let j = i + 1; j < xySegments.length; j++) {
          const [B1, B2] = xySegments[j];
          const inter = lineSegmentIntersection2D(A1, A2, B1, B2);
          if (inter) intersectionPointsXY.push(inter);
        }
      }

      const intersectionPointsLL = intersectionPointsXY.map((pt) =>
        xyToLatLng(pt.x, pt.y, centerRef.lat, centerRef.lng)
      );

      setMarmaPoints(intersectionPointsLL);
      setMarmaLines(results);
      setAngleMarkers(intersections);
    } else {
      setAngleMarkers([]);
    }
  }, [pins]);

  const disableMapDrag = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.setOptions({ draggable: false });
    }
  }, []);
  const enableMapDrag = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.setOptions({ draggable: true });
    }
  }, []);

  const pushPinsToHistory = (newPins: any[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newPins);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setPins(newPins);
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setPins(history[newIndex]);
    }
  };
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setPins(history[newIndex]);
    }
  };

  const onLoadAutocomplete = (
    autocompleteInstance: google.maps.places.Autocomplete
  ) => {
    setAutocomplete(autocompleteInstance);
  };
  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.geometry) {
        const location = {
          lat: place.geometry.location?.lat() || defaultCenter.lat,
          lng: place.geometry.location?.lng() || defaultCenter.lng,
        };
        setMapCenter(location);
        setZoom(21);
      }
    }
  };

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;

    // matching site plan scenario
    if (
      isMatchingSitePlanPoints &&
      currentSitePlanPointIndex < sitePlanPoints.length
    ) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const newMapPoint = { lat, lng };

      // Confirm if we’re overriding an existing point
      if (mapPoints[currentSitePlanPointIndex]) {
        const existingPoint = mapPoints[currentSitePlanPointIndex];
        const distance = calculateDistance(
          lat,
          lng,
          existingPoint.lat,
          existingPoint.lng
        );
        if (distance < 0.5) {
          const confirmed = window.confirm(
            "You've pinned a point close to an existing one. Overwrite?"
          );
          if (!confirmed) return;
        }
      }
      setMapPoints((prev) => {
        const updated = [...prev];
        updated[currentSitePlanPointIndex] = newMapPoint;
        return updated;
      });

      const nextIndex = currentSitePlanPointIndex + 1;
      if (nextIndex >= sitePlanPoints.length) {
        setIsMatchingSitePlanPoints(false);
        setCurrentSitePlanPointIndex(0);
      } else {
        setCurrentSitePlanPointIndex(nextIndex);
      }
      return;
    }

    // normal pinning
    if (isPinning) {
      const newPin = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
        isHovered: false,
        isSitePlanSelected: false,
      };
      pushPinsToHistory([...pins, newPin]);
    }
  };

  function pixelToLatLng(x: number, y: number): google.maps.LatLng | null {
    if (!mapRef.current) return null;
    const projection = mapRef.current.getProjection();
    if (!projection) return null;

    const bounds = mapRef.current.getBounds();
    if (!bounds) return null;

    const scale = Math.pow(2, mapRef.current.getZoom()!);
    const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
    const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
    if (!topRight || !bottomLeft) return null;

    const worldPoint = new window.google.maps.Point(
      x / scale + bottomLeft.x,
      y / scale + topRight.y
    );
    return projection.fromPointToLatLng(worldPoint);
  }

  const handleMarkerDrag = (e: google.maps.MapMouseEvent, index: number) => {
    if (!e.latLng) return;
    const newLat = e.latLng.lat();
    const newLng = e.latLng.lng();

    const updatedPins = [...pins];
    updatedPins[index] = { ...updatedPins[index], lat: newLat, lng: newLng };
    pushPinsToHistory(updatedPins);
  };

  const handleMarkerHover = (index: number) => {
    setPins((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isHovered: true };
      return updated;
    });
  };
  const handleMarkerOut = (index: number) => {
    setPins((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isHovered: false };
      return updated;
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const itemId = e.dataTransfer.getData("item_id");
    const itemType = e.dataTransfer.getData("item_type");
    const itemName = e.dataTransfer.getData("item_name");

    if (!itemId || !mapRef.current) return;
    const mapContainer = mapContainerRef.current;
    if (!mapContainer) return;

    const rect = mapContainer.getBoundingClientRect();
    const dropX = e.clientX - rect.left;
    const dropY = e.clientY - rect.top;
    const latLng = pixelToLatLng(dropX, dropY);
    if (!latLng) return;

    let newItem: DroppedItem = {
      id: `${itemId}-${Date.now()}`,
      type: itemType,
      displayName: itemName,
      lat: latLng.lat(),
      lng: latLng.lng(),
      color: null,
      width: 50,
      height: 50,
      rotation: 0,
      directions: [],
    };

    // If it’s a Room, bigger dimension
    if (itemType === "Room") {
      newItem.width = 150;
      newItem.height = 50;
    }

    // If the user’s item is something else, you can do default width/height or override color
    if (
      itemType === "kitchenBurnerSlab" ||
      itemType === "toiletSeat" ||
      itemType === "housePillar" ||
      itemType === "wall" ||
      itemType === "entrances"
    ) {
      const whiteColor = colors?.find((c) => c.systemName === "white");
      if (whiteColor) newItem.color = whiteColor.id;
    }

    // Now compute directions for the newly dropped item
    if (compassCenter && directions && mapRef.current) {
      const directionArr = getDirectionsForItem(
        newItem,
        mapRef.current,
        compassCenter,
        directions
      );
      newItem.directions = directionArr;
      if (itemType === "entrances" && directionArr.length > 0) {
        const angle = getAngleFromCentre(compassCenter, {
          lat: newItem.lat ?? 0,
          lng: newItem.lng ?? 0,
        });

        const directionObj = directions.find((d) => d.id === directionArr[0]);
        if (directionObj?.entrances?.length) {
          const closestSubzone = getClosestEntrance(
            angle,
            directionObj.entrances
          );
          console.log("📦 Closest Entrance:", {
            lat: newItem.lat,
            lng: newItem.lng,
            closestSubzone,
          });

          newItem.entranceSubzones = closestSubzone;
        }
      }
    }

    setDroppedItems((prev) => [...prev, newItem]);
  };

  const handleTouchDrop = (
    itemId: string,
    itemName: string,
    itemType: string,
    clientX: number,
    clientY: number
  ) => {
    if (!mapRef.current) return;
    const mapContainer = mapContainerRef.current;
    if (!mapContainer) return;

    const rect = mapContainer.getBoundingClientRect();
    const dropX = clientX - rect.left;
    const dropY = clientY - rect.top;
    const latLng = pixelToLatLng(dropX, dropY);
    if (!latLng) return;

    let newItem: DroppedItem = {
      id: `${itemId}+${Date.now()}`,
      type: itemType,
      displayName: itemName,
      lat: latLng.lat(),
      lng: latLng.lng(),
      width: 50,
      height: 50,
      rotation: 0,
      color: null,
      directions: [],
    };

    if (itemType === "Room") {
      newItem.width = 150;
      newItem.height = 50;
    } else if (
      itemType === "kitchenBurnerSlab" ||
      itemType === "toiletSeat" ||
      itemType === "housePillar" ||
      itemType === "wall" ||
      itemType === "entrances"
    ) {
      const whiteColor = colors?.find((c) => c.systemName === "white");
      if (whiteColor) newItem.color = whiteColor.id;
    }

    // Compute directions
    if (compassCenter && directions && mapRef.current) {
      const directionArr = getDirectionsForItem(
        newItem,
        mapRef.current,
        compassCenter,
        directions
      );
      newItem.directions = directionArr;
    }

    setDroppedItems((prev) => [...prev, newItem]);
  };

  const handleItemMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    itemId: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    disableMapDrag();
    setDraggingItemId(itemId);

    const item = droppedItems.find((itm) => itm.id === itemId);
    if (!item) return;
    setInitialLatLng({ lat: item.lat ?? 0, lng: item.lng ?? 0 });
    setInitialMouse({ x: e.clientX, y: e.clientY });
  };

  const handleRotateMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    itemId: string
  ) => {
    e.stopPropagation();
    e.preventDefault();
    disableMapDrag();

    setRotatingItemId(itemId);
    const item = droppedItems.find((itm) => itm.id === itemId);
    if (!item) return;

    setInitialRotation(item.rotation || 0);
    setInitialMouse({ x: e.clientX, y: e.clientY });
  };

  const handleResizeMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    itemId: string,
    corner: Corner
  ) => {
    e.preventDefault();
    e.stopPropagation();
    disableMapDrag();

    setResizingItemId(itemId);
    setResizingCorner(corner);

    const item = droppedItems.find((itm) => itm.id === itemId);
    if (!item) return;

    setInitialSize({
      width: item.width ?? 50,
      height: item.height ?? 50,
    });
    setInitialMouse({ x: e.clientX, y: e.clientY });
  };

  const handleTouchStartItem = (
    e: React.TouchEvent<HTMLDivElement>,
    itemId: string
  ) => {
    e.stopPropagation();
    e.preventDefault();
    disableMapDrag();

    setDraggingItemId(itemId);
    const item = droppedItems.find((itm) => itm.id === itemId);
    if (!item) return;

    const touch = e.touches[0];
    setInitialLatLng({ lat: item.lat ?? 0, lng: item.lng ?? 0 });
    setInitialMouse({ x: touch.clientX, y: touch.clientY });
  };

  const handleResizeTouchStart = (
    e: React.TouchEvent<HTMLDivElement>,
    itemId: string,
    corner: Corner
  ) => {
    e.stopPropagation();
    e.preventDefault();
    disableMapDrag();

    setResizingItemId(itemId);
    setResizingCorner(corner);

    const item = droppedItems.find((itm) => itm.id === itemId);
    if (!item) return;

    setInitialSize({
      width: item.width ?? 50,
      height: item.height ?? 50,
    });

    const touch = e.touches[0];
    setInitialMouse({ x: touch.clientX, y: touch.clientY });
  };

  const handleRotateTouchStart = (
    e: React.TouchEvent<HTMLDivElement>,
    itemId: string
  ) => {
    e.stopPropagation();
    e.preventDefault();
    disableMapDrag();

    setRotatingItemId(itemId);
    const item = droppedItems.find((itm) => itm.id === itemId);
    if (!item) return;

    setInitialRotation(item.rotation || 0);
    const touch = e.touches[0];
    setInitialMouse({ x: touch.clientX, y: touch.clientY });
  };

  const handleCutItem = (itemId: string) => {
    setDroppedItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  function pixelDeltaToLatLngDelta(dx: number, dy: number) {
    if (!mapRef.current) {
      return { latDelta: 0, lngDelta: 0 };
    }
    const projection = mapRef.current.getProjection();
    if (!projection) {
      return { latDelta: 0, lngDelta: 0 };
    }

    const point = projection.fromLatLngToPoint(
      new window.google.maps.LatLng(initialLatLng.lat, initialLatLng.lng)
    );
    const scale = 1 << mapRef.current.getZoom()!;
    if (!point) {
      return { latDelta: 0, lngDelta: 0 };
    }

    point.x += dx / scale;
    point.y += dy / scale;

    const newLatLng = projection.fromPointToLatLng(point);
    if (!newLatLng) {
      return { latDelta: 0, lngDelta: 0 };
    }
    return {
      latDelta: newLatLng.lat() - initialLatLng.lat,
      lngDelta: newLatLng.lng() - initialLatLng.lng,
    };
  }

  const handleItemContextMenu = (e: React.MouseEvent, itemId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedItemId(itemId);
    setColorAnchorEl(e.currentTarget as HTMLElement);
    setColorMenuOpen(true);
  };

  function getSignX(corner: Corner) {
    // if corner has "Left" => we want to adjust width in the negative direction
    // if corner has "Right" => positive
    if (corner.includes("Right")) return +1;
    return -1;
  }
  function getSignY(corner: Corner) {
    // if corner has "top" => negative
    // if corner has "bottom" => positive
    if (corner.includes("bottom")) return +1;
    return -1;
  }

  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (draggingItemId) {
        const dx = e.clientX - initialMouse.x;
        const dy = e.clientY - initialMouse.y;
        const { latDelta, lngDelta } = pixelDeltaToLatLngDelta(dx, dy);

        setDroppedItems((prev) =>
          prev.map((item) => {
            if (item.id === draggingItemId) {
              const updatedItem = {
                ...item,
                lat: initialLatLng.lat + latDelta,
                lng: initialLatLng.lng + lngDelta,
              };
              // Recompute directions in real-time
              if (compassCenter && directions && mapRef.current) {
                const dirArr = getDirectionsForItem(
                  updatedItem,
                  mapRef.current,
                  compassCenter,
                  directions
                );
                updatedItem.directions = dirArr;
                // ✅ Recompute entranceSubzones on drag
                if (updatedItem.type === "entrances" && dirArr.length > 0) {
                  const angle = getAngleFromCentre(compassCenter, {
                    lat: updatedItem.lat ?? 0,
                    lng: updatedItem.lng ?? 0,
                  });

                  const directionObj = directions.find(
                    (d) => d.id === dirArr[0]
                  );
                  if (directionObj?.entrances?.length) {
                    updatedItem.entranceSubzones = getClosestEntrance(
                      angle,
                      directionObj.entrances
                    );
                  }
                }
              }

              return updatedItem;
            }
            return item;
          })
        );
      }

      if (resizingItemId && resizingCorner) {
        const dx = e.clientX - initialMouse.x;
        const dy = e.clientY - initialMouse.y;
        const sx = getSignX(resizingCorner);
        const sy = getSignY(resizingCorner);

        setDroppedItems((prev) =>
          prev.map((item) => {
            if (item.id === resizingItemId) {
              // New width/height
              let newWidth = initialSize.width + sx * dx;
              let newHeight = initialSize.height + sy * dy;

              if (item.type === "wall") {
                // For walls, allow down to 1px
                newWidth = Math.max(1, newWidth);
                newHeight = Math.max(1, newHeight);
              } else {
                // For other items, maintain minimum size of 20px
                newWidth = Math.max(20, newWidth);
                newHeight = Math.max(20, newHeight);
              }

              const updatedItem = {
                ...item,
                width: newWidth,
                height: newHeight,
              };

              // Recompute directions
              if (compassCenter && directions && mapRef.current) {
                const dirArr = getDirectionsForItem(
                  updatedItem,
                  mapRef.current,
                  compassCenter,
                  directions
                );
                updatedItem.directions = dirArr;
              }
              return updatedItem;
            }
            return item;
          })
        );
      }

      if (rotatingItemId) {
        setDroppedItems((prev) =>
          prev.map((itm) => {
            if (itm.id === rotatingItemId) {
              const element = document.getElementById(rotatingItemId);
              if (!element) return itm;

              const rect = element.getBoundingClientRect();
              const centerX = rect.left + rect.width / 2;
              const centerY = rect.top + rect.height / 2;

              const currentAngle =
                Math.atan2(e.clientY - centerY, e.clientX - centerX) *
                (180 / Math.PI);
              const angleDiff = currentAngle - initialRotation;
              const newRotation = (itm.rotation || 0) + angleDiff;

              const updatedItem = { ...itm, rotation: newRotation };

              // Recompute directions in real-time
              if (compassCenter && directions && mapRef.current) {
                const dirArr = getDirectionsForItem(
                  updatedItem,
                  mapRef.current,
                  compassCenter,
                  directions
                );
                updatedItem.directions = dirArr;
              }

              // update local states used to measure angle changes
              setInitialRotation(currentAngle);
              setInitialMouse({ x: e.clientX, y: e.clientY });
              return updatedItem;
            }
            return itm;
          })
        );
      }
    };

    const handleGlobalMouseUp = () => {
      if (draggingItemId) {
        setDraggingItemId(null);
        enableMapDrag();
      }
      if (resizingItemId) {
        setResizingItemId(null);
        setResizingCorner(null);
        enableMapDrag();
      }
      if (rotatingItemId) {
        setRotatingItemId(null);
        enableMapDrag();
      }
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 0) return;
      e.preventDefault();

      const touch = e.touches[0];
      if (draggingItemId) {
        // DRAG
        const dx = touch.clientX - initialMouse.x;
        const dy = touch.clientY - initialMouse.y;
        const { latDelta, lngDelta } = pixelDeltaToLatLngDelta(dx, dy);

        setDroppedItems((prev) =>
          prev.map((item) => {
            if (item.id === draggingItemId) {
              const updatedItem = {
                ...item,
                lat: initialLatLng.lat + latDelta,
                lng: initialLatLng.lng + lngDelta,
              };
              if (compassCenter && directions && mapRef.current) {
                const dirArr = getDirectionsForItem(
                  updatedItem,
                  mapRef.current,
                  compassCenter,
                  directions
                );
                updatedItem.directions = dirArr;
              }
              return updatedItem;
            }
            return item;
          })
        );
      }

      if (resizingItemId && resizingCorner) {
        // RESIZE
        const dx = touch.clientX - initialMouse.x;
        const dy = touch.clientY - initialMouse.y;
        const sx = getSignX(resizingCorner);
        const sy = getSignY(resizingCorner);

        setDroppedItems((prev) =>
          prev.map((item) => {
            if (item.id === resizingItemId) {
              let newWidth = initialSize.width + sx * dx;
              let newHeight = initialSize.height + sy * dy;

              newWidth = Math.max(20, newWidth);
              newHeight = Math.max(20, newHeight);

              const updatedItem = {
                ...item,
                width: newWidth,
                height: newHeight,
              };

              // Recompute directions
              if (compassCenter && directions && mapRef.current) {
                const dirArr = getDirectionsForItem(
                  updatedItem,
                  mapRef.current,
                  compassCenter,
                  directions
                );
                updatedItem.directions = dirArr;
              }
              return updatedItem;
            }
            return item;
          })
        );
      }

      if (rotatingItemId) {
        setDroppedItems((prev) =>
          prev.map((itm) => {
            if (itm.id === rotatingItemId) {
              const element = document.getElementById(rotatingItemId);
              if (!element) return itm;

              const rect = element.getBoundingClientRect();
              const centerX = rect.left + rect.width / 2;
              const centerY = rect.top + rect.height / 2;

              const currentAngle =
                Math.atan2(touch.clientY - centerY, touch.clientX - centerX) *
                (180 / Math.PI);
              const angleDiff = currentAngle - initialRotation;
              const newRotation = (itm.rotation || 0) + angleDiff;

              const updatedItem = { ...itm, rotation: newRotation };

              if (compassCenter && directions && mapRef.current) {
                const dirArr = getDirectionsForItem(
                  updatedItem,
                  mapRef.current,
                  compassCenter,
                  directions
                );
                updatedItem.directions = dirArr;
              }

              setInitialRotation(currentAngle);
              setInitialMouse({ x: touch.clientX, y: touch.clientY });
              return updatedItem;
            }
            return itm;
          })
        );
      }
    };

    const handleGlobalTouchEnd = () => {
      if (draggingItemId) {
        setDraggingItemId(null);
        enableMapDrag();
      }
      if (resizingItemId) {
        setResizingItemId(null);
        setResizingCorner(null);
        enableMapDrag();
      }
      if (rotatingItemId) {
        setRotatingItemId(null);
        enableMapDrag();
      }
    };

    document.addEventListener("mousemove", handleGlobalMouseMove);
    document.addEventListener("mouseup", handleGlobalMouseUp);

    document.addEventListener("touchmove", handleGlobalTouchMove, {
      passive: false,
    });
    document.addEventListener("touchend", handleGlobalTouchEnd, {
      passive: false,
    });

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("touchmove", handleGlobalTouchMove);
      document.removeEventListener("touchend", handleGlobalTouchEnd);
    };
  }, [
    draggingItemId,
    rotatingItemId,
    resizingItemId,
    resizingCorner,
    initialLatLng,
    initialMouse,
    initialSize,
    initialRotation,
    droppedItems,
    enableMapDrag,
    directions,
    compassCenter,
  ]);

  const handleColorSelect = (color: string) => {
    setDroppedItems((prev) =>
      prev.map((item) =>
        item.id === selectedItemId ? { ...item, color } : item
      )
    );
    setColorMenuOpen(false);
  };

  const toggleMenu = () => setIsOpen(!isOpen);
  const toggleShowCompass = () => setShowCompass((prev) => !prev);
  const toggleShowMarma = () => setShowMarma((prev) => !prev);

  const confirmClearMap = () => setOpenClearDialog(true);
  const handleClearMap = () => {
    setPins([]);
    setDroppedItems([]);
    setOpenClearDialog(false);
    setIsPinning(true);
  };

  const mapContainerStyle = {
    width: "100%",
    height: isMobile ? "calc(100vh - 56px)" : "100%",
  };

  const handleSaveMapState = () => {
    if (!propertyId) return;
    const requestBody = {
      pins,
      droppedItems,
      // sitePlanPoints,
      // mapPoints,
    };
    const tenantId = currentUser.tenant.id;

    saveMapState(
      { requestBody, propertyId, tenantId, context: "map" },
      {
        onSuccess: () => {
          toast.success("Map state saved successfully!");
        },
        onError: () => {
          toast.error("Failed to save map state.");
        },
      }
    );
  };

  useBeforeunload(() => {
    // Attempt to save the map state before leaving
    handleSaveMapState();
    return "Are you sure you want to leave? Unsaved changes may be lost.";
  });

  if (isFetching) {
    return <div>Loading map data...</div>;
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Top center Autocomplete */}
      <Box
        sx={{
          position: "absolute",
          top: isMobile ? "5px" : "10px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          width: isMobile ? "90%" : "80%",
          maxWidth: "400px",
        }}
      >
        <Autocomplete
          onLoad={onLoadAutocomplete}
          onPlaceChanged={onPlaceChanged}
        >
          <input
            type="text"
            placeholder="Search for a location"
            style={{
              width: "100%",
              padding: isMobile ? "8px" : "10px",
              border: "none",
              outline: "none",
              borderRadius: "8px",
              fontSize: isMobile ? "14px" : "16px",
            }}
          />
        </Autocomplete>
      </Box>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: isMobile ? "calc(100vh - 56px)" : "100vh",
        }}
      >
        <div
          ref={mapContainerRef}
          style={{ width: "100%", height: "100%", position: "relative" }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={zoom}
            onClick={handleMapClick}
            onLoad={(map) => {
              mapRef.current = map;
              map.setMapTypeId("hybrid");
            }}
            options={{
              gestureHandling: "greedy",
              zoomControl: !isMobile,
              streetViewControl: !isMobile,
              mapTypeControl: !isMobile,
              fullscreenControl: !isMobile,
            }}
          >
            {pins.length < 1 && <Marker position={mapCenter} />}

            {pins.map((pin, i) => {
              if (i === pins.length - 1) return null;
              const isHovered = hoveredEdge === i;
              const isSelected = selectedEdges.includes(i);
              const strokeColor = isSelected
                ? "blue"
                : isHovered
                ? "green"
                : pin.color;
              return (
                <Polyline
                  key={i}
                  path={[pin, pins[i + 1]]}
                  options={{
                    strokeColor,
                    strokeWeight: 4,
                    strokeOpacity: 1.0,
                  }}
                  onMouseOver={() => {
                    setHoveredEdge(i);
                    setSelectedEdges([]);
                  }}
                  onMouseOut={() => setHoveredEdge(null)}
                />
              );
            })}

            {mapPoints.map((point, i) => {
              if (i === mapPoints.length - 1) return null;
              return (
                <Polyline
                  key={`mp-${i}`}
                  path={[point, mapPoints[i + 1]]}
                  options={{
                    strokeColor: "blue",
                    strokeWeight: 4,
                    strokeOpacity: 1.0,
                  }}
                />
              );
            })}

            {marmaLines &&
              showMarma &&
              !isPinning &&
              marmaLines.map((coords: LatLng[], index: number) => (
                <Polyline
                  key={index}
                  path={coords}
                  options={{
                    strokeColor: "#FF0000",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                  }}
                />
              ))}

            {!showCompass &&
              (isPinning
                ? pins.map((pin, i) => (
                    <Marker
                      key={i}
                      position={{ lat: pin.lat, lng: pin.lng }}
                      icon={{
                        url: pinSvg,
                        scaledSize: new window.google.maps.Size(
                          isMobile ? 20 : 25,
                          isMobile ? 20 : 25
                        ),
                      }}
                      draggable
                      onDrag={(e) => handleMarkerDrag(e, i)}
                      onClick={() => {
                        if (i === 0 && pins.length > 2) {
                          setIsPinning(false);
                          setPins((prevPins) => [
                            ...prevPins,
                            { lat: prevPins[0].lat, lng: prevPins[0].lng },
                          ]);
                          if (mapRef.current) {
                            mapRef.current.setMapTypeId("roadmap");
                            setZoom(24);
                            mapRef.current.setCenter({
                              lat: compassCenter?.lat ?? 0,
                              lng: compassCenter?.lng ?? 0,
                            });
                          }
                        }
                      }}
                    />
                  ))
                : pins.map((pin, i) => (
                    <Marker
                      key={i}
                      position={{ lat: pin.lat, lng: pin.lng }}
                      icon={{
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: isMobile ? 4 : 6,
                        fillColor: pin.isSitePlanSelected
                          ? "#0000ff"
                          : pin.isHovered
                          ? "#00ff00"
                          : "#ff0000",
                        fillOpacity: 1,
                        strokeWeight: 1,
                        strokeColor: "#000",
                      }}
                      draggable
                      onMouseOver={() => handleMarkerHover(i)}
                      onMouseOut={() => handleMarkerOut(i)}
                      onDrag={(e) => handleMarkerDrag(e, i)}
                      onClick={() => {
                        // If matching site plan, allow user to pick the pin
                        if (isMatchingSitePlanPoints) {
                          const userConfirmed = window.confirm(
                            "Do you want to select this pin for site plan matching?"
                          );
                          setPins((prevPins) => {
                            const updated = [...prevPins];
                            if (userConfirmed) {
                              updated[i].isSitePlanSelected = true;
                            }
                            return updated;
                          });
                          if (userConfirmed && mapPoints.length < 2) {
                            setMapPoints((prev) => [...prev, pin]);
                          }
                        }
                      }}
                    />
                  )))}

            {marmaPoints &&
              showMarma &&
              !isPinning &&
              marmaPoints.map((marma: LatLng, i: number) => (
                <Marker
                  key={i}
                  position={{ lat: marma.lat, lng: marma.lng }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 6,
                    fillOpacity: 1,
                    strokeWeight: 1,
                    strokeColor: "#000",
                  }}
                />
              ))}

            {angleMarkers &&
              showMarma &&
              !isPinning &&
              angleMarkers.map((point, idx) => (
                <Marker
                  key={`angle-marker-${idx}`}
                  position={{ lat: point.lat, lng: point.lng }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 5,
                    fillColor: "#0000ff",
                    fillOpacity: 1,
                    strokeWeight: 1,
                    strokeColor: "#fff",
                  }}
                />
              ))}

            {!isPinning && showCompass && compassCenter && (
              <OverlayView
                position={{ lat: compassCenter.lat, lng: compassCenter.lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <Box
                  sx={{
                    transform: "translate(-50%, -50%)",
                    opacity: compassOpacity,
                    pointerEvents: "none",
                  }}
                >
                  <Compass />
                </Box>
              </OverlayView>
            )}

            <DroppedItemsOverlay
              droppedItems={droppedItems}
              onMouseDown={handleItemMouseDown}
              onContextMenu={handleItemContextMenu}
              onResizeMouseDown={handleResizeMouseDown}
              onRotateMouseDown={handleRotateMouseDown}
              onCutItem={handleCutItem}
              onTouchStart={handleTouchStartItem}
              onResizeTouchStart={handleResizeTouchStart}
              onRotateTouchStart={handleRotateTouchStart}
              colors={colors}
            />
          </GoogleMap>
        </div>

        {!isOpen && (
          <IconButton
            onClick={toggleMenu}
            sx={{
              position: "absolute",
              top: isMobile ? "5%" : "10%",
              right: 0,
              transform: "translateY(-50%)",
              backgroundColor: "black",
              color: "white",
              zIndex: 1200,
              padding: isMobile ? "6px" : "8px",
            }}
          >
            <MenuIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        )}

        <ActivityBar
          isOpen={isOpen}
          toggleMenu={toggleMenu}
          onCompassOpacityChange={setCompassOpacity}
          showMarma={showMarma}
          showCompass={showCompass}
          toggleShowCompass={toggleShowCompass}
          toggleShowMarma={toggleShowMarma}
          handleUndo={handleUndo}
          handleRedo={handleRedo}
          canUndo={historyIndex > 0}
          canRedo={historyIndex < history.length - 1}
          isPinning={isPinning}
          droppedItems={droppedItems}
          directions={directions}
          sitePlanImage={sitePlanImage}
          setSitePlanImage={setSitePlanImage}
          sitePlanPoints={sitePlanPoints}
          setSitePlanPoints={setSitePlanPoints}
          mapPoints={mapPoints}
          setMapPoints={setMapPoints}
          isMatchingSitePlanPoints={isMatchingSitePlanPoints}
          setIsMatchingSitePlanPoints={setIsMatchingSitePlanPoints}
          isMobile={isMobile}
          isTablet={isTablet}
          marmaPoints={marmaPoints}
          handleSaveMapState={handleSaveMapState}
          confirmClearMap={confirmClearMap}
          onItemDrop={handleTouchDrop}
        />

        <ColorMenu
          anchorEl={colorAnchorEl}
          open={colorMenuOpen}
          onClose={() => setColorMenuOpen(false)}
          colors={colors ?? []}
          onColorSelect={handleColorSelect}
          getColorHex={(cId) => getColorHexCode(cId, colors ?? [])}
        />

        <ConfirmDialog
          open={openClearDialog}
          title="Clear Map"
          description="Are you sure you want to clear all pins and dropped items? This action cannot be undone."
          onConfirm={handleClearMap}
          onCancel={() => setOpenClearDialog(false)}
        />
      </Box>
    </Box>
  );
}

export default function Home() {
  return (
    <div>
      {typeof window !== "undefined" && (window as any).google ? (
        <MapContent />
      ) : (
        <LoadScript
          googleMapsApiKey={import.meta.env.VITE_API_KEY}
          libraries={["places"]}
        >
          <MapContent />
        </LoadScript>
      )}
    </div>
  );
}
